import type { UpsertUserOverridePayload } from '@mesterplan/api-client';
import type { PermissionKey } from '@mesterplan/shared-types';

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Guid gate for the per-user permission-override cutover: a Guid-keyed company + Guid-keyed
 * user are .NET-native and their override lives in the API; a legacy Firestore push-id on
 * either side keeps reading/writing the `USER_PERMISSION_OVERRIDES` subcollection. The id
 * shape alone decides the source. (Admin/team role config is deferred — see PR notes.)
 */
export function isGuid(value: string | null | undefined): value is string {
  return typeof value === 'string' && GUID_PATTERN.test(value);
}

/**
 * The user-override wire shape: granted = keys forced ON over the role/team default,
 * revoked = keys forced OFF. A flat `{ key: boolean }` map (the legacy Firestore document
 * shape) splits into the two lists; `true` → granted, `false` → revoked.
 */
export function flatMapToGrantedRevoked(
  flat: Partial<Record<PermissionKey, boolean>>,
): UpsertUserOverridePayload {
  const granted: string[] = [];
  const revoked: string[] = [];
  for (const [key, value] of Object.entries(flat)) {
    if (value === true) {
      granted.push(key);
    } else if (value === false) {
      revoked.push(key);
    }
  }
  return { granted, revoked };
}

/**
 * Inverse of {@link flatMapToGrantedRevoked}: reconstruct the flat `{ key: boolean }` map the
 * SPA renders from the API's granted/revoked lists. granted → `true`, revoked → `false`.
 */
export function grantedRevokedToFlatMap(
  granted: readonly string[],
  revoked: readonly string[],
): Partial<Record<PermissionKey, boolean>> {
  const flat: Partial<Record<PermissionKey, boolean>> = {};
  for (const key of granted) {
    flat[key as PermissionKey] = true;
  }
  for (const key of revoked) {
    flat[key as PermissionKey] = false;
  }
  return flat;
}
